/**
 * CC7Result — compatibility result screen.
 * data-screen-label="cc-07 Result"
 *
 * - Hero score 96px Spectral with gradient text (scoreOverall)
 * - Archetype Spectral italic 24px
 * - 4 sub-score bars with aria-valuenow/valuemin/valuemax + role="progressbar"
 * - 500-word report markdown rendered via ReportMarkdown (react-markdown + remark-gfm)
 * - Sticky bar: "Share card" + "Couple Portrait · +50 ⭐" upsell
 * - Renders within 200ms of run response (no heavy ops on mount)
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { CCBackdrop, CCAvatar } from './shared';
import { useChart } from '../../../hooks/useChart';
import { useImageFeatures } from '../../../hooks/useImageFeatures';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import { ReportMarkdown } from '../../ReportMarkdown';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// Sub-score config
// --------------------------------------------------------------------------

interface SubScore {
  labelKey: string;
  icon: string;
  key: 'scoreLove' | 'scoreFriendship' | 'scoreCommunication' | 'scoreConflict';
  color: string;
}

const SUB_SCORES: SubScore[] = [
  { labelKey: 'compat.result.sub.romantic',      icon: '♥', key: 'scoreLove',          color: '#ff8eb0' },
  { labelKey: 'compat.result.sub.friendship',    icon: '✶', key: 'scoreFriendship',     color: '#c89cff' },
  { labelKey: 'compat.result.sub.communication', icon: '◐', key: 'scoreCommunication',  color: '#90caf9' },
  { labelKey: 'compat.result.sub.conflict',      icon: '⚡', key: 'scoreConflict',       color: '#e89a3c' },
];

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC7ResultProps {
  flow: UseCompatFlowReturn;
}

export function CC7Result({ flow }: CC7ResultProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('flows');
  const { output, partner } = flow;
  // The user's own archetype comes from their natal chart (the flow only
  // carries the partner). Falls back to a neutral "you" label.
  const { data: chart } = useChart();
  const { enabled: imagesOn } = useImageFeatures();
  const myArchetype = chart?.status === 'ready' ? chart.archetype?.label ?? null : null;

  function handleShare() {
    flow.send({ type: 'SHARE' });
  }

  function handleUpsell() {
    const username = partner?.username;
    const dest = username
      ? `/flow/couple?partnerUsername=${encodeURIComponent(username)}&upsell=1`
      : '/flow/couple?upsell=1';
    void navigate(dest);
  }

  if (!output) {
    return (
      <div className={styles.root} data-screen-label="cc-07 Result">
        <CCBackdrop tone="pair" />
        <div className={styles.content}>
          <div className={styles.computingCenter}>
            <div className={styles.eyebrow}>{t('compat.result.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  const overall = Math.round(output.scoreOverall);
  const myGlyph = '🌙';
  const partnerGlyph = partner?.glyph ?? '✦';
  const partnerPalette = partner?.palette ?? 'violet';

  return (
    <div className={styles.root} data-screen-label="cc-07 Result">
      <CCBackdrop tone="pair" />

      <div className={styles.content} style={{ padding: 0 }}>
        {/* Scrollable area */}
        <div className={styles.resultScrollArea}>
          {/* Hero score */}
          <div className={styles.heroScore}>
            <div className={styles.heroEyebrow}>
              {myArchetype ?? t('compat.pair.me')} × {partner?.archetype ?? t('compat.pair.partnerFallback')}
            </div>
            <div className={styles.heroNumber}>
              {overall}<span className={styles.heroPct}>%</span>
            </div>
            <div className={styles.heroArchetype}>
              {t('compat.result.heroArchetype')}
            </div>
            <div className={styles.heroSubtext}>
              {t('compat.result.heroSubtext')}
            </div>
          </div>

          {/* Avatars side by side */}
          <div className={styles.resultAvatarRow}>
            <div className={styles.resultAvatarLeft}>
              <CCAvatar glyph={myGlyph} palette="gold" size={56} />
            </div>
            <div className={styles.resultTimesGlyph}>×</div>
            <div className={styles.resultAvatarRight}>
              <CCAvatar glyph={partnerGlyph} palette={partnerPalette} size={56} />
            </div>
          </div>

          {/* Sub-scores */}
          <div className={styles.subScoresSection}>
            <div className={styles.subScoresHeader}>
              <span className={styles.eyebrow}>{t('compat.result.subScores')}</span>
              <span className={styles.sectionRule} />
            </div>

            {SUB_SCORES.map((s) => {
              const pct = Math.round(output[s.key]);
              return (
                <div key={s.key} className={styles.scoreRow}>
                  <span className={styles.scoreIcon} style={{ color: s.color }}>
                    {s.icon}
                  </span>
                  <div>
                    <div className={styles.scoreLabel}>{t(s.labelKey)}</div>
                    <div className={styles.scoreBarWrap}>
                      <div
                        className={styles.scoreBar}
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${s.color}aa, ${s.color})`,
                        }}
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={t('compat.result.scoreAria', { label: t(s.labelKey), pct })}
                      />
                    </div>
                  </div>
                  <div className={styles.scoreValue}>
                    {pct}<span className={styles.scorePctUnit}>%</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Report */}
          <div className={styles.reportSection}>
            <div className={styles.reportHeader}>
              <span className={styles.eyebrow}>{t('compat.result.reportTitle')}</span>
              <span className={styles.sectionRule} />
              <span className={styles.reportByline}>{t('compat.result.byline')}</span>
            </div>
            <ReportMarkdown markdown={output.reportMarkdown ?? ''} />
          </div>

          {/* Spacer so sticky bar doesn't overlap last content */}
          <div style={{ height: 80 }} />
        </div>

        {/* Sticky bar */}
        <div className={styles.resultStickyBar}>
          <button
            type="button"
            className={styles.shareBtn}
            onClick={handleShare}
            data-testid="cc7-share-btn"
          >
            ↗ {t('compat.result.shareCard')}
          </button>
          {imagesOn && (
            <button
              type="button"
              className={styles.upsellBtn}
              onClick={handleUpsell}
              data-testid="cc7-upsell-btn"
            >
              ❀ {t('compat.result.upsell', { stars: SKU_CATALOG['couple_portrait_upsell'].stars })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
